import _ from 'lodash'
import axios from '@/services/axios'
import dealsData from '@/data/deals'
import router from '@/router'
import { checkIsInterestIncomplete } from '@/helpers/interest'

const SET_INTEREST = 'SET_INTEREST'
const SET_FILTERS = 'SET_FILTERS'
const SET_DEALS = 'SET_DEALS'
const SET_PAGINATION = 'SET_PAGINATION'
const CLEANUP = 'CLEANUP'

const state = {
  interest: {},
  deals: null,
  filters: null,
  pagination: null,
  hits: 0
}

const mutations = {
  [SET_INTEREST] (state, interest) {
    state.interest = interest
  },
  [SET_DEALS] (state, { deals, hits }) {
    state.deals = deals
    state.hits = hits
  },
  [SET_FILTERS] (state, filters) {
    state.filters = filters
  },
  [SET_PAGINATION] (state, pagination) {
    state.pagination = pagination
  },
  [CLEANUP] (state, pagination) {
    state.pagination = null
    state.filters = null
    state.deals = null
    state.interest = null
  }
}

const getters = {
  interest: state => state.interest,
  list: state => state.deals,
  hits: state => state.hits,
  pagination: state => state.pagination,
  filters: state => state.filters || {
    ..._.pick(state.interest, ['industrySectors', 'dealRanges', 'regionalFocus', 'transactionType', 'dealStructure']),
    transactionType: _.get(state, 'interest.other.transactionType')
  },
  isInterestIncomplete: state => checkIsInterestIncomplete(state.interest)
}

const platforms = 'crossboundary_pa'

const actions = {
  filters ({ commit, dispatch }, selections) {
    commit('SET_FILTERS', selections)
    return dispatch('list')
  },
  list ({ commit, dispatch, state }) {
    const interest = getters.interest(state)
    const selections = _.cloneDeep(getters.filters(state))
    const pagination = getters.pagination(state)

    if (!interest || !interest._id) {
      return commit(SET_DEALS, { deals: dealsData, hits: dealsData.length })
    }

    const filters = Object.keys(selections).reduce((result, key) => {
      if (!selections[key] || selections[key].length === 0) return result
      if ([].concat(selections[key] && key !== 'transactionType').includes('all')) return result // all means any

      result[key] = selections[key]
      return result
    }, {})

    const structure = (filters.dealStructure || '').toLowerCase()
    if (structure === 'debt') filters.isDebtInvestment = true
    if (structure === 'equity') filters.isEquityInvestment = true
    if (structure === 'dual: debt and equity') {
      filters.isDebtInvestment = true
      filters.isEquityInvestment = true
    }

    delete filters.dealStructure

    if (['all', 'trade'].includes(filters.transactionType)) {
      filters.isTradeDeal = true
    }
    if (['all', 'investment'].includes(filters.transactionType)) {
      filters.isEquityInvestment = true
    }
    if (['all', 'lpEquity'].includes(filters.transactionType)) {
      filters.isLpEquity = true
    }

    delete filters.transactionType

    filters.platforms = platforms

    return axios.post('/search2', {
      kind: ['dealroom_interest'],
      offset: ((pagination || {}).page || 0) * 10,
      query: {
        bool: {
          must_not: {
            terms: {
              _id: ((interest || {}).deals || []).map(d => d._id)
            }
          },
          must: Object.keys(filters).map(key => ({
            bool: {
              should: _.uniq([].concat(filters[key])).map(value => ({ term: { [`${key}.lowerKeyword`]: value } }))
            }
          }))
        }
      },
      count: 10
    }).then(resp => {
      commit(SET_DEALS, { deals: _.get(resp, 'data.data'), hits: _.get(resp, 'data.pagination.total') })
      return (resp.data || {}).data
    })
  },
  total ({ commit, dispatch, state }) {
    const interest = getters.interest(state)
    return axios.post('/search2', {
      kind: ['dealroom_interest'],
      query: {
        bool: {
          must_not: {
            terms: {
              _id: ((interest || {}).deals || []).map(d => d._id)
            }
          },
          must: {
            bool: {
              should: { term: { 'platforms.lowerKeyword': platforms } }
            }
          }
        }
      },
      count: 1
    }).then(resp => {
      commit(SET_PAGINATION, _.get(resp, 'data.pagination'))
      return (resp.data || {}).data
    })
  },
  update ({ commit, dispatch }, { interest }) {
    return axios.put(`/dealroom-interest/${interest._id}`, interest).then((resp) => {
      commit(SET_INTEREST, resp.data)
      return resp.data
    })
  },
  get ({ commit, dispatch }) {
    return axios.get('/dealroom-interests?platform=crossboundary').then((resp) => {
      const interest = (resp.data || { items: [] }).items[0]
      if (interest && !interest.other) interest.other = {}
      commit(SET_INTEREST, interest)
      if (checkIsInterestIncomplete(interest)) {
        router.push('/edit-profile').catch(() => {})
      }
      return interest
    })
  },
  create ({ commit, dispatch }, interest) {
    return axios.post('/dealroom-interests', interest).then((response) => {
      const interest = response.data
      commit(SET_INTEREST, interest)
      if (checkIsInterestIncomplete(interest)) {
        router.push('/edit-profile').catch(() => {})
      }
      return interest
    })
  },
  ensure ({ commit, dispatch, rootGetters }, interest) {
    const user = rootGetters['user/currentUser']

    return dispatch('get').then((result) => {
      if (result) return result

      return dispatch('create', {
        company: {
          name: '<not specified>',
          website: '<not specified>',
          baseCountry: '<not specified>'
        },
        contactPerson: {
          givenName: user.firstName,
          familyName: user.lastName,
          email: user.email,
          telephone: '<not specified>'
        },
        investorType: 'private',
        platform: 'crossboundary',
        userId: user.userId,
        other: {}
      })
    })
  }
}

export default {
  namespaced: true,
  actions,
  mutations,
  state,
  getters
}
